import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import styles from "./Footer.module.css";
import FabButton from "../Fab/Fab";
import { useTranslation } from "react-i18next";
import lngs from "../../lngs";
import {
  Container,
  Grid,
  Typography,
  Box,
  Link as MuiLink,
  IconButton,
  Divider,
  Button,
} from "@mui/material";
import {
  Facebook,
  Twitter,
  Linkedin,
  Instagram,
  Mail,
  Phone,
  MapPin,
  ArrowRight,
  Globe,
  MessageCircle,
} from "lucide-react";

const Footer = () => {
  const location = useLocation();
  const [user, setUser] = useState(JSON.parse(localStorage.getItem("profile")));
  const { t, i18n } = useTranslation();
  const currentLang = i18n.resolvedLanguage;
  const isApp = location.pathname
    .toLowerCase()
    .includes(`/${currentLang}/app`.toLowerCase());

  useEffect(() => {
    setUser(JSON.parse(localStorage.getItem("profile")));
  }, [location]);

  const currentYear = new Date().getFullYear();

  if (isApp) return null;

  return (
    <Box component="footer" className={styles.footer}>
      <Container maxWidth="lg">
        {/* Main Footer Content */}
        <Grid container spacing={4} className={styles.footerMainContent}>
          {/* Company Info */}
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ mb: 2 }}>
              <img
                src="/logo.svg"
                alt="Quantum Billing"
                style={{ height: 75 }}
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                  e.currentTarget.nextSibling.style.display = "block";
                }}
              />
              <Typography
                variant="h6"
                className={styles.footerHeading}
                style={{ display: "none" }}
              >
                Quantum Billing
              </Typography>
            </Box>
            <Typography variant="body2" className={styles.footerDescription}>
              {t("advanced_erp_billing_solutions_description")}
            </Typography>
            <Box className={styles.socialIcons}>
              <IconButton aria-label="Facebook" size="small" sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "#4DB6AC" } }}>
                <Facebook size={20} />
              </IconButton>
              <IconButton aria-label="Twitter" size="small" sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "#4DB6AC" } }}>
                <Twitter size={20} />
              </IconButton>
              <IconButton aria-label="LinkedIn" size="small" sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "#4DB6AC" } }}>
                <Linkedin size={20} />
              </IconButton>
              <IconButton aria-label="Instagram" size="small" sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "#4DB6AC" } }}>
                <Instagram size={20} />
              </IconButton>
            </Box>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="h6" className={styles.footerHeading}>
              {t("quick_links")}
            </Typography>
            <ul className={styles.footerLinks}>
              <li>
                <ArrowRight size={14} className={styles.linkIcon} />
                <MuiLink href={`/${currentLang}/`} underline="hover">
                  {t("home")}
                </MuiLink>
              </li>
              <li>
                <ArrowRight size={14} className={styles.linkIcon} />
                <MuiLink href={`/${currentLang}/about`} underline="hover">
                  {t("about_us")}
                </MuiLink>
              </li>
              <li>
                <ArrowRight size={14} className={styles.linkIcon} />
                <MuiLink href={`/${currentLang}/services`} underline="hover">
                  {t("services")}
                </MuiLink>
              </li>
              <li>
                <ArrowRight size={14} className={styles.linkIcon} />
                <MuiLink href={`/${currentLang}/prices`} underline="hover">
                  {t("pricing")}
                </MuiLink>
              </li>
              <li>
                <ArrowRight size={14} className={styles.linkIcon} />
                <MuiLink href={`/${currentLang}/contactus`} underline="hover">
                  {t("contact")}
                </MuiLink>
              </li>
              <li>
                <ArrowRight size={14} className={styles.linkIcon} />
                <MuiLink href={`/${currentLang}/privacy-policy`} underline="hover">
                  {t("privacy_policy")}
                </MuiLink>
              </li>
              <li>
                <ArrowRight size={14} className={styles.linkIcon} />
                <MuiLink href={`/${currentLang}/contactus`} underline="hover">
                  {t("support")}
                </MuiLink>
              </li>
              <li>
                <ArrowRight size={14} className={styles.linkIcon} />
                <MuiLink href={`/${currentLang}/prices`} underline="hover">
                  {t("try_for_free")}
                </MuiLink>
              </li>
            </ul>
          </Grid>

          {/* Services */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="h6" className={styles.footerHeading}>
              {t("our_services")}
            </Typography>
            <ul className={styles.footerLinks}>
              <li>
                <ArrowRight size={14} className={styles.linkIcon} />
                <MuiLink href={`/${currentLang}/services#erp`} underline="hover">
                  {t("erp_solutions")}
                </MuiLink>
              </li>
              <li>
                <ArrowRight size={14} className={styles.linkIcon} />
                <MuiLink href={`/${currentLang}/services#billing`} underline="hover">
                  {t("billing_management")}
                </MuiLink>
              </li>
              <li>
                <ArrowRight size={14} className={styles.linkIcon} />
                <MuiLink href={`/${currentLang}/services#inventory`} underline="hover">
                  {t("inventory_control")}
                </MuiLink>
              </li>
              <li>
                <ArrowRight size={14} className={styles.linkIcon} />
                <MuiLink href={`/${currentLang}/services#accounting`} underline="hover">
                  {t("financial_analytics")}
                </MuiLink>
              </li>
              <li>
                <ArrowRight size={14} className={styles.linkIcon} />
                <MuiLink href={`/${currentLang}/services#cloud`} underline="hover">
                  {t("cloud_solutions")}
                </MuiLink>
              </li>
              <li>
                <ArrowRight size={14} className={styles.linkIcon} />
                <MuiLink
                  href="http://holo-line.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                >
                  Holo Line
                </MuiLink>
              </li>
            </ul>
          </Grid>

          {/* Contact Info */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="h6" className={styles.footerHeading}>
              {t("contact_us")}
            </Typography>
            <Box className={styles.contactInfo}>
              <Box className={styles.contactItem}>
                <MapPin size={18} className={styles.contactIcon} />
                <Typography variant="body2">
                  123 Business Avenue, Tech Park, CA 94103
                </Typography>
              </Box>
              <Box className={styles.contactItem}>
                <Phone size={18} className={styles.contactIcon} />
                <MuiLink
                  href="tel://+1361-405-5427"
                  underline="hover"
                  className={styles.contactLink}
                >
                  +1 361-405-5427
                </MuiLink>
              </Box>
              <Box className={styles.contactItem}>
                <Mail size={18} className={styles.contactIcon} />
                <MuiLink
                  href="mailto:info@Quantum-Billing.com"
                  underline="hover"
                  className={styles.contactLink}
                >
                  info@Quantum-Billing.com
                </MuiLink>
              </Box>
              <Box className={styles.contactItem}>
                <Globe size={18} className={styles.contactIcon} />
                <MuiLink
                  href="https://www.Quantum-Billing.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  className={styles.contactLink}
                >
                  www.Quantum-Billing.com
                </MuiLink>
              </Box>
              <Button
                href="https://wa.me/13614055427"
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                startIcon={<MessageCircle size={18} />}
                className={styles.whatsappButton}
              >
                {t("chat_whatsapp_now")}
              </Button>
            </Box>
          </Grid>
        </Grid>

        <Divider className={styles.footerDivider} />

        {/* Newsletter Section */}
        <Box className={styles.newsletterSection}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h6" className={styles.newsletterHeading}>
                {t("subscribe_to_newsletter")}
              </Typography>
              <Typography variant="body2">
                {t("stay_updated_with_latest_news")}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box className={styles.subscribeForm}>
                <input
                  type="email"
                  placeholder={t("your_email_address")}
                  className={styles.subscribeInput}
                />
                <Button
                  variant="contained"
                  color="primary"
                  className={styles.subscribeButton}
                >
                  {t("subscribe")}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Divider className={styles.footerDivider} />

        {/* Copyright Section */}
        <Box className={styles.copyrightSection}>
          <Typography variant="body2" align="center" sx={{ color: "rgba(255,255,255,0.5)" }}>
            &copy; {currentYear} Quantum Billing | {t("made_with")} &hearts;{" "}
            {t("by")} {t("quantum_billing_made_limited")}
          </Typography>
          <Box className={styles.footerBottomLinks}>
            <MuiLink href={`/${currentLang}/privacy-policy`} underline="hover" className={styles.bottomLink}>
              {t("privacy_policy")}
            </MuiLink>
            <MuiLink href={`/${currentLang}/terms-of-service`} underline="hover" className={styles.bottomLink}>
              {t("terms_of_service")}
            </MuiLink>
            <MuiLink href={`/${currentLang}/cookie-policy`} underline="hover" className={styles.bottomLink}>
              {t("cookie_policy")}
            </MuiLink>
          </Box>
          <Box
            className={styles.languageLinks}
            aria-label="Available languages"
          >
            {Object.entries(lngs).map(([code, language]) => (
              <a
                key={code}
                href={`/${code}/`}
                className={styles.languageLink}
                hrefLang={code}
                lang={code}
                title={language.nativeName}
                aria-label={`View Quantum Billing in ${language.nativeName}`}
              >
                {code.toUpperCase()}
              </a>
            ))}
          </Box>
        </Box>
      </Container>
      {user && <FabButton />}
    </Box>
  );
};

export default Footer;
